import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import httpClient from "../../utils/httpClient";
import { removeOrderIdFromLocal } from "../../utils/common";

interface RequestDetails {
  Action: string;
  OrderId?: number; // Optional for initial fetch
  shareEstimate?: boolean;
}

export interface SKU {
  SkuId: string;
  ProductId: string;
  ProductSkuNum: number;
  Name: string;
  Description: string;
  CostMacroCategoryCd: string;
  CostMetricCd: string;
  CostMetricCost: number;
  CurrencyCd: string;
  CostIntervalCd: string;
  CostIntervalMin: number;
  CostIntervalMax: number;
  IsAutoProvisioned: boolean;
  IsReturnable: boolean;
  StatusCd: string;
  CreatedUser: string;
  UpdatedUser: string;
  CreatedDtm: string;
  UpdatedDtm: string;
  OrderQuantity: number;
  OrderInterval: number;
  SkuType?: string;
  SkuRelation: {
    SkuId: string;
    ProductId: string;
    ProductSkuNum: number;
    Name: string;
    Description: string;
    CostMacroCategoryCd: string;
    CostMetricCd: string;
    CostMetricCost: number;
    CurrencyCd: string;
    CostIntervalCd: string;
    CostIntervalMin: number;
    CostIntervalMax: number;
    IsAutoProvisioned: boolean;
    IsReturnable: boolean;
    StatusCd: string;
    CreatedUser: string;
    UpdatedUser: string;
    CreatedDtm: string;
    UpdatedDtm: string;
    OrderQuantity: number;
    OrderInterval: number;
    SkuType?: string;
    SkuRelation: [];
  }[];
}

export interface ResponseDetails {
  OrderId: number;
  OrderStatus: string;
  Description: string;
  EngagementId: string;
  OpportunityId: string;
  CreatedByEmail: string;
  CRMSaveDtm: string;
  IsCRMSyncRequired: boolean;
  Details: {
    ProductName: string;
    AssetAdvice: string;
    ProductId: string;
    SkuDetails: SKU[];
  }[];
}

interface CheckoutState {
  isLoading: boolean;
  data: ResponseDetails | null;
  estimateData?: ResponseDetails | null; // Optional field for estimate data
  error: string | null;
  isEstimateChanged?: boolean; // Flag to indicate if the estimate has been changed
  reloadPage?: boolean; // Flag to indicate if the page should be reloaded
  showDiscardModal?: boolean; // Flag to indicate if the discard modal should be shown
  isLogout?: boolean; // Flag to indicate if the user is logging out
}

const initialState: CheckoutState = {
  isLoading: true,
  data: null,
  estimateData: null, // Initialize estimateData as null
  error: null,
  isEstimateChanged: false, // Initialize isEstimateChanged as false
  reloadPage: false, // Initialize reloadPage as false
  showDiscardModal: false, // Initialize showDiscardModal as false
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const fetchCheckoutData = createAsyncThunk<any, RequestDetails>(
  "CheckoutSlice/fetchCheckoutData",
  async (requestDetails, thunkApi) => {
    const requestParam = {
      Action: requestDetails.Action,
      OrderId: requestDetails.OrderId || 0, // Use 0 if OrderId is not provided
      ShareEstimate: requestDetails.shareEstimate || false,
    };
    try {
      const response = await httpClient.post("/order", requestParam);
      if (response.status != 200 || response.data.Status !== "success") {
        return thunkApi.rejectWithValue({
          message: "Failed to get checkout data",
        });
      } else {
        if (
          response?.data?.Message === "no_order_found" &&
          response?.data?.Code === 404
        ) {
          return thunkApi.rejectWithValue({
            message: "delete_estimate",
          });
        } else if (
          response?.data?.Message === "404_no_estimate_found" &&
          response?.data?.Code === 404
        ) {
          return thunkApi.rejectWithValue({
            message: "delete_estimate",
          });
        } else if (response?.data?.Code === 404) {
          // remove OrderId from local storage if the order is not found, to avoid issue when user try to access checkout page with the deleted orderId in local storage
          removeOrderIdFromLocal();
        } else {
          return response.data;
        }
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      if (error.status === 401) {
        return thunkApi.rejectWithValue({
          message: "access_estimate",
        });
      } else if (error.status === 404) {
        // remove OrderId from local storage if the order is not found, to avoid issue when user try to access checkout page with the deleted orderId in local storage
        removeOrderIdFromLocal();
      } else {
        return thunkApi.rejectWithValue({
          message: "Failed to get checkout data",
        });
      }
    }
  },
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface OrderNowRequestDetails {
  Action: string;
  OrderId: number;
  OpportunityId: string;
  OrderSku: [
    {
      SkuId: string;
      EngagementId: string;
      ParentSkuId: string;
    },
  ];
}

export const callOrderNow = createAsyncThunk<any, OrderNowRequestDetails>(
  "CheckoutSlice/callOrderNow",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post("/order", {
        Action: requestDetails.Action,
        OrderId: requestDetails.OrderId,
        OrderSku: requestDetails.OrderSku,
        OpportunityId: requestDetails.OpportunityId,
      });

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to call Order Now",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      if (
        error.response?.data?.Status &&
        error.response?.data?.Status == "error"
      ) {
        return thunkApi.rejectWithValue({
          message: error.response.data.Message,
          payload: error.response.data.Data,
        });
      } else {
        console.error("🚀 ~ error:", error);
        return thunkApi.rejectWithValue({
          message: "Failed to call Order Now",
        });
      }
    }
  },
);

export interface OrderSkuDetails {
  SkuId: string;
  ParentSkuId?: string;
  Quantity: number;
  Intervals: number;
  BillingType?: string;
}
export interface OrderUpdateRequestDetails {
  Action: string;
  OrderId: number;
  Description: string;
  OrderSku?: OrderSkuDetails[];
}

export const updateOrderBundle = createAsyncThunk<
  any,
  OrderUpdateRequestDetails
>("CheckoutSlice/updateOrderBundle", async (requestDetails, thunkApi) => {
  try {
    const response = await httpClient.post("/order", {
      Action: requestDetails.Action,
      OrderId: requestDetails.OrderId,
      Description: requestDetails.Description,
      // OrderSku: requestDetails.OrderSku,
    });
    if (response.status != 200 || response.data.Status !== "success") {
      if (response.data.Status == "error") {
        return thunkApi.rejectWithValue({
          message: response.data.Message,
        });
      } else {
        return thunkApi.rejectWithValue({
          message: "Failed to call update Order Bundle",
        });
      }
    }
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.log("🚀 ~ error:", error);
    return thunkApi.rejectWithValue({
      message: "Failed to call Order Now",
    });
  }
});

export const updateOrderBundleList = createAsyncThunk<
  any,
  OrderUpdateRequestDetails
>("CheckoutSlice/updateOrderBundleList", async (requestDetails, thunkApi) => {
  try {
    const response = await httpClient.post("/order", {
      Action: requestDetails.Action,
      OrderId: requestDetails.OrderId,
      // Description: requestDetails.Description,
      OrderSku: requestDetails.OrderSku,
    });
    if (response.status != 200 || response.data.Status !== "success") {
      if (response.data.Status == "error") {
        return thunkApi.rejectWithValue({
          message: response.data.Message,
        });
      } else {
        return thunkApi.rejectWithValue({
          message: "Failed to call update Order Bundle",
        });
      }
    }
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.log("🚀 ~ error:", error);
    return thunkApi.rejectWithValue({
      message: "Failed to call Order Now",
    });
  }
});

interface DeleteProductInOrderBundleListRequestDetails {
  Action: string;
  OrderId: number;
  ProductId: string;
}

export const deleteProductInOrderBundleList = createAsyncThunk<
  any,
  DeleteProductInOrderBundleListRequestDetails
>(
  "CheckoutSlice/deleteProductInOrderBundleList",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post("/order", {
        Action: requestDetails.Action,
        OrderId: Number(requestDetails.OrderId),
        ProductId: requestDetails.ProductId,
      });
      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to call update Order Bundle",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      return thunkApi.rejectWithValue({
        message: "Failed to call Order Now",
      });
    }
  },
);

interface DeleteOrderBundleRequestDetails {
  Action: string;
  OrderId: number;
}

export const deleteOrderBundle = createAsyncThunk<
  any,
  DeleteOrderBundleRequestDetails
>("CheckoutSlice/deleteOrderBundle", async (requestDetails, thunkApi) => {
  try {
    const response = await httpClient.post("/order", {
      Action: requestDetails.Action,
      OrderId: Number(requestDetails.OrderId),
    });
    if (response.status != 200 || response.data.Status !== "success") {
      if (response.data.Status == "error") {
        return thunkApi.rejectWithValue({
          message: response.data.Message,
        });
      } else {
        return thunkApi.rejectWithValue({
          message: "Failed to call update Order Bundle",
        });
      }
    }
    return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.log("🚀 ~ error:", error);
    return thunkApi.rejectWithValue({
      message: "Failed to call Order Now",
    });
  }
});

export const callSaveEstimate = createAsyncThunk<any, OrderNowRequestDetails>(
  "CheckoutSlice/callSaveEstimate",
  async (requestDetails, thunkApi) => {
    try {
      const response = await httpClient.post("/order", {
        Action: requestDetails.Action,
        OrderId: requestDetails.OrderId,
        OrderSku: requestDetails.OrderSku,
        OpportunityId: requestDetails.OpportunityId,
      });

      if (response.status != 200 || response.data.Status !== "success") {
        if (response.data.Status == "error") {
          return thunkApi.rejectWithValue({
            message: response.data.Message,
          });
        } else {
          return thunkApi.rejectWithValue({
            message: "Failed to call Save Estimte Api",
          });
        }
      }
      return response.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log("🚀 ~ error:", error);
      if (
        error.response?.data?.Status &&
        error.response?.data?.Status == "error"
      ) {
        return thunkApi.rejectWithValue({
          message: error.response.data.Message,
          payload: error.response.data.Data,
        });
      } else {
        console.error("🚀 ~ error:", error);
        return thunkApi.rejectWithValue({
          message: "Failed to call Save Estimte Api",
        });
      }
    }
  },
);

const CheckoutSlice = createSlice({
  name: "CheckoutSlice",
  initialState,
  reducers: {
    resetCheckoutSate: (state) => {
      state.isLoading = false;
      state.data = null;
      state.estimateData = null; // Reset estimateData as well
      state.error = null;
      state.isEstimateChanged = false; // Reset isEstimateChanged as well
      state.reloadPage = false;
    },
    updateEstimateChangeState: (state) => {
      state.isEstimateChanged = true;
      state.reloadPage = false;
    },
    resetEstimateChangeState: (state) => {
      state.isEstimateChanged = false; // Reset isEstimateChanged as well
      state.reloadPage = true; // Reset reloadPage as well
    },
    resetRelaodPageState: (state) => {
      state.reloadPage = false; // Reset reloadPage as well`
    },
    updateShowDiscardModalState: {
      reducer: (
        state,
        action: PayloadAction<{ value: boolean; isLogout?: boolean }>,
      ) => {
        state.showDiscardModal = action.payload.value;
        state.isLogout = action.payload.isLogout || false;
      },
      prepare: (value: boolean, isLogout?: boolean) => ({
        payload: { value, isLogout },
      }),
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCheckoutData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
        state.isEstimateChanged = false; // Reset isEstimateChanged as well
      })
      .addCase(
        fetchCheckoutData.fulfilled,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          if (action.payload.Status == "success") {
            state.isLoading = false;
            state.data = action.payload.Data;
            state.error = null;
            state.isEstimateChanged = false; // Reset isEstimateChanged as well
          } else if (action.payload.Status == "error") {
            state.isLoading = false;
            state.data = null;
            state.error = action.payload.message;
            state.isEstimateChanged = false; // Reset isEstimateChanged as well
          }
        },
      )
      .addCase(
        fetchCheckoutData.rejected,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state, action: PayloadAction<any>) => {
          state.isLoading = false;
          state.data = null;
          state.error = action?.payload?.message;
          state.isEstimateChanged = false; // Reset isEstimateChanged as well
        },
      );
  },
});

export const {
  resetCheckoutSate,
  updateEstimateChangeState,
  resetEstimateChangeState,
  resetRelaodPageState,
  updateShowDiscardModalState,
} = CheckoutSlice.actions;

export default CheckoutSlice.reducer;
